/**
 * L-NAV-3 — redirections des deux anciennes pages admin d'une campagne.
 *
 * La fiche campagne `/campaigns/:id` porte déjà les onglets Paramètres et
 * Équipe ; les pages `/admin/campaigns/:id/settings` (réglages d'appel) et
 * `/admin/campaigns/:id/assignments` (affectation des agents) en étaient des
 * doublons. Leur contenu a été porté dans les onglets, les pages supprimées ;
 * les anciens chemins vivent encore dans des signets, des liens partagés et
 * l'historique : ils REDIRIGENT vers l'onglet, ils ne cassent pas.
 *
 * - `replace` : pas d'entrée d'historique (sinon « précédent » boucle) ;
 * - la query de l'ancien lien est conservée, son `tab` éventuel cède à
 *   l'onglet cible ;
 * - `location.state` suit : `state.from` (#259) ramène le fil d'ariane de la
 *   fiche campagne à la page d'origine (fiche client).
 *
 * Gardes : la route cible porte sa `ProtectedRoute` (`manageCampaigns`).
 * Les réglages portés restent réservés au rôle ADMIN DANS l'onglet (garde de
 * l'ancienne route), voir `CampaignCallSettings`.
 *
 * Helper exporté (pas écrit en clair dans `App.tsx`) pour que
 * `legacyCampaignAdminRedirects.spec.tsx` monte les VRAIES routes.
 */
import { Navigate, Route, useLocation, useParams } from 'react-router-dom'

type CampaignTab = 'settings' | 'team'

/**
 * ÉCRANS-RÉGLAGES (#451) — l'onglet Paramètres est un maître-détail : l'ancienne
 * page des RÉGLAGES D'APPEL ouvre la section qui les porte (`section=appels`).
 */
export const LEGACY_CAMPAIGN_ADMIN_REDIRECTS: ReadonlyArray<{ from: string; tab: CampaignTab; section?: string }> = [
  { from: '/admin/campaigns/:id/settings', tab: 'settings', section: 'appels' },
  { from: '/admin/campaigns/:id/assignments', tab: 'team' },
]

function RedirectToCampaignTab({ tab, section }: { tab: CampaignTab; section?: string }) {
  const { id = '' } = useParams<{ id: string }>()
  const location = useLocation()
  const search = new URLSearchParams(location.search)
  search.set('tab', tab)
  if (section) search.set('section', section)
  return (
    <Navigate
      to={{ pathname: `/campaigns/${encodeURIComponent(id)}`, search: `?${search.toString()}` }}
      state={location.state}
      replace
    />
  )
}

/** Routes à insérer dans l'arbre AUTHENTIFIÉ, avant le `*` de la 404. */
export function legacyCampaignAdminRedirects() {
  return LEGACY_CAMPAIGN_ADMIN_REDIRECTS.map(({ from, tab, section }) => (
    <Route key={from} path={from} element={<RedirectToCampaignTab tab={tab} section={section} />} />
  ))
}
